import type { AvailabilityMode, Granularity, WorkerMonthCell, WorkerSkillEntry, WorkerView } from '@/types/ui/resource-availability'

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function monthLabel(monthStart: string): string {
  const d = new Date(monthStart)
  return MONTH_LABELS[d.getUTCMonth()]
}

/** Short weekday + month + day for a single ISO date, e.g. "Mon, Apr 7". UTC-parsed to
 *  match the rest of this module (dates are date-only, no timezone shift). */
export function formatDayLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return `${WEEKDAY_LABELS[d.getUTCDay()]}, ${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCDate()}`
}

/** `formatDayLabel` plus the year, e.g. "Mon, Apr 7, 2026". Used where the date can sit
 *  outside the current year — heatmap windows span multiple years, so a year-less end date
 *  would be ambiguous. */
export function formatDayLabelWithYear(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return `${formatDayLabel(iso)}, ${d.getUTCFullYear()}`
}

export function monthLabelFull(monthStart: string): string {
  const d = new Date(monthStart)
  return `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

export function isCurrentMonth(monthStart: string): boolean {
  const now = new Date()
  const d = new Date(monthStart)
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth()
}

/**
 * Whether the supplied ISO week-Monday date falls in the same week as today.
 */
export function isCurrentWeek(weekStart: string): boolean {
  const now = new Date()
  const todayWeekStart = mondayOf(now)
  const cellWeekStart = new Date(weekStart)
  return (
    cellWeekStart.getUTCFullYear() === todayWeekStart.getUTCFullYear() &&
    cellWeekStart.getUTCMonth() === todayWeekStart.getUTCMonth() &&
    cellWeekStart.getUTCDate() === todayWeekStart.getUTCDate()
  )
}

/**
 * Full-range label for a weekly bucket. `Apr 7–13` style — same month for the common
 * case, falls back to `Apr 28–May 4` when the week straddles a month boundary. Used
 * for tooltips / accessible names where space isn't constrained.
 */
export function weekLabel(weekStartIso: string): string {
  const start = new Date(weekStartIso)
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 6) // Monday + 6 = Sunday
  const startMonth = MONTH_LABELS[start.getUTCMonth()]
  const endMonth = MONTH_LABELS[end.getUTCMonth()]
  const startDay = start.getUTCDate()
  const endDay = end.getUTCDate()
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}–${endDay}`
  }
  return `${startMonth} ${startDay}–${endMonth} ${endDay}`
}

/**
 * Month-tier label for a *group* of weekly buckets (the colspan covering every week
 * whose Monday falls in a single calendar month). Standard groups get the abbreviated
 * month name ("Mar") with the year carried in a separate suffix slot. The edge case
 * is a single-week group whose only week straddles a month boundary — labelling it
 * just "Mar" would lie about the rest of the column. We expand bridge-only groups
 * to "Mar–Apr"; cross-year bridges fold both years into the label inline so the
 * single yearSuffix slot isn't ambiguous.
 *
 * Multi-week groups never use the bridge form: the bridge is always the *last* week,
 * the per-week day-range tier already shows the cross-month dates, and the group
 * itself is anchored squarely in the start month.
 */
export function weekGroupHeader(
  firstWeekStartIso: string,
  count: number
): { label: string; yearSuffix: string | null } {
  const ws = new Date(firstWeekStartIso + 'T00:00:00Z')
  const startMonth = MONTH_LABELS[ws.getUTCMonth()]
  const startYearSuffix = String(ws.getUTCFullYear()).slice(2)
  if (count !== 1) return { label: startMonth, yearSuffix: startYearSuffix }
  const we = new Date(ws)
  we.setUTCDate(we.getUTCDate() + 6)
  if (we.getUTCMonth() === ws.getUTCMonth()) {
    return { label: startMonth, yearSuffix: startYearSuffix }
  }
  const endMonth = MONTH_LABELS[we.getUTCMonth()]
  if (we.getUTCFullYear() === ws.getUTCFullYear()) {
    return { label: `${startMonth}–${endMonth}`, yearSuffix: startYearSuffix }
  }
  // Cross-year bridge (e.g. Dec 28 → Jan 3): render both years inline so the trailing
  // single-year suffix doesn't misrepresent the second half.
  const endYearSuffix = String(we.getUTCFullYear()).slice(2)
  return {
    label: `${startMonth} ’${startYearSuffix}–${endMonth} ’${endYearSuffix}`,
    yearSuffix: null,
  }
}

/**
 * Day-only range label for a weekly bucket (e.g. `4–10`, `27–3`). Designed to sit
 * under a separate month-name header tier in the heatmap, so the month context isn't
 * needed in the cell itself. Cross-month weeks show start-day–end-day; the slice's
 * month-tier label clarifies which month the week starts in.
 */
export function weekDayRange(weekStartIso: string): string {
  const start = new Date(weekStartIso)
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 6)
  return `${start.getUTCDate()}–${end.getUTCDate()}`
}

function mondayOf(d: Date): Date {
  const utcDay = d.getUTCDay() // Sun=0, Mon=1, ..., Sat=6
  const offset = (utcDay - 1 + 7) % 7
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - offset))
  return monday
}

function toIsoDate(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, '0')
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Enumerate every Monday in `[startIso, endIso]` (inclusive). Includes the Monday before
 * `startIso` if it isn't already a Monday — partial leading weeks aren't lost. Used by
 * the heatmap to produce the visible-column list (which may exceed currently-loaded data
 * when an incremental fetch is in flight).
 */
export function weeksInRange(startIso: string, endIso: string): string[] {
  const start = new Date(startIso)
  const cursor = mondayOf(start)
  const end = new Date(endIso)
  const out: string[] = []
  while (cursor <= end) {
    out.push(toIsoDate(cursor))
    cursor.setUTCDate(cursor.getUTCDate() + 7)
  }
  return out
}

/**
 * Enumerate every month-start (yyyy-MM-01) in `[startIso, endIso]` (inclusive). Same role
 * as `weeksInRange` for the monthly heatmap granularity.
 */
export function monthsInRange(startIso: string, endIso: string): string[] {
  const start = new Date(startIso)
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1))
  const end = new Date(endIso)
  const out: string[] = []
  while (cursor <= end) {
    out.push(toIsoDate(cursor))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return out
}

/**
 * Last day a heatmap bucket covers: Sunday for a weekly (Monday-anchored) bucket, month-end
 * for a monthly one. Buckets on the axis are contiguous, so this is only actually needed for
 * the final column — every other bucket's end is the day before the next bucket's start.
 */
export function bucketEndIso(bucketStartIso: string, granularity: Granularity): string {
  const d = new Date(bucketStartIso + 'T00:00:00Z')
  const end = granularity === 'weekly'
    ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 6))
    : new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
  return toIsoDate(end)
}

/**
 * Where a worker's end date falls on the visible heatmap axis.
 *
 * `endIndex` is the column whose bucket contains that date — the one that carries the end
 * marker — or -1 when the date precedes the whole axis, where no column owns it. `greyFromIndex`
 * is the first column lying entirely after it; every column from there on greys out, and it
 * equals the column count when nothing greys.
 */
export interface RowEndMarker {
  endIndex: number
  greyFromIndex: number
}

/**
 * Resolve the end marker for one heatmap row against the visible bucket starts (ascending,
 * contiguous, all the same granularity).
 *
 * Returns null in the two cases where there's nothing to say:
 * - No known end date (T4-salaried workers not pending termination, since their profile has
 *   no end date field). Greying would assert an end the payload doesn't support.
 * - End date past the axis: their capacity doesn't end anywhere on screen.
 *
 * A date BEFORE the axis does produce a marker (endIndex -1, whole row greys). That's meaningful:
 * the worker's profile expired or their pending termination date passed before this window, so
 * they have no capacity across all of it, and a worker still on the dashboard past that date is
 * worth surfacing. The row's date chip carries the date since no column can.
 */
export function resolveRowEndMarker(
  bucketStarts: string[],
  profileEndDate: string | undefined,
  granularity: Granularity
): RowEndMarker | null {
  if (!profileEndDate || bucketStarts.length === 0) return null
  // ISO yyyy-MM-dd compares lexicographically, so a bucket starting after the end date is
  // wholly post-end. Buckets are ascending, so the first such index is the grey line.
  const greyFromIndex = bucketStarts.findIndex(start => start > profileEndDate)
  if (greyFromIndex === -1) {
    // Nothing greys: the end date is either inside the final visible bucket (mark it) or past
    // the axis entirely (nothing to show).
    const lastIndex = bucketStarts.length - 1
    const withinLastBucket = profileEndDate <= bucketEndIso(bucketStarts[lastIndex], granularity)
    return withinLastBucket
      ? { endIndex: lastIndex, greyFromIndex: bucketStarts.length }
      : null
  }
  // greyFromIndex 0 → the end predates the axis, so no column owns the date (-1) and every
  // column greys.
  return { endIndex: greyFromIndex - 1, greyFromIndex }
}

export function workerInitials(name: string): string {
  const trimmedName = name?.trim() ?? ''
  if (!trimmedName) return '?'
  const parts = trimmedName.split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Color buckets for heatmap cells. Returns Tailwind class names so the FE never has to
 * reach into theme variables.
 *
 * Allocated mode targets high utilization: 0-33 red, 34-66 orange, 67-100 green, >100 red.
 * Available mode inverts that mapping (low availability = high utilization = green).
 */
export function heatmapClasses(
  cell: WorkerMonthCell,
  mode: AvailabilityMode
): { bg: string; fg: string } {
  // 0% allocated = no engagement coverage this month → highlight in purple to keep these
  // cells visually distinct from the "high availability" bucket below. In Available mode this
  // only applies when the cell is genuinely fully available: post-GW-230 a cell can be 0%
  // allocated yet <100% available (PTO/stat consuming capacity), and those must fall through
  // to the availability scale rather than read as fully-open purple.
  if (cell.allocatedPercent === 0 && (mode === 'allocated' || cell.availablePercent === 100)) {
    return { bg: 'bg-purple-100', fg: 'text-purple-800' }
  }

  const RED = { bg: 'bg-red-200', fg: 'text-red-900' }
  const ORANGE = { bg: 'bg-orange-200', fg: 'text-orange-900' }
  const GREEN = { bg: 'bg-green-200', fg: 'text-green-900' }

  const pct = mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent

  if (mode === 'allocated') {
    if (pct > 100) return RED
    if (pct < 34) return RED
    if (pct < 67) return ORANGE
    return GREEN
  }
  // available mode — mirror allocated: negative availability means overallocated, treat
  // as red so a worker booked over capacity reads the same in both modes.
  if (pct < 0) return RED
  if (pct < 34) return GREEN
  if (pct < 67) return ORANGE
  return RED
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`
}

export function formatHours(value: number): string {
  return `${Math.round(value)}h`
}

export function teamAveragePercent(workers: WorkerView[], monthIndex: number, mode: AvailabilityMode): number {
  if (workers.length === 0) return 0
  const total = workers.reduce((sum, w) => {
    const cell = w.months[monthIndex]
    if (!cell) return sum
    return sum + (mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent)
  }, 0)
  return Math.round(total / workers.length)
}

/**
 * Average percent across every (worker, month) cell whose `monthStart` falls in
 * `[startIso, endIso]`. Used by KPI cards to reflect whatever heatmap window the
 * user is currently looking at — single-month windows reduce to per-month averages.
 */
export function teamAveragePercentInRange(
  workers: WorkerView[],
  startIso: string,
  endIso: string,
  mode: AvailabilityMode
): number {
  if (workers.length === 0) return 0
  let total = 0
  let count = 0
  for (const w of workers) {
    for (const cell of w.months) {
      if (cell.monthStart < startIso || cell.monthStart > endIso) continue
      total += mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent
      count++
    }
  }
  if (count === 0) return 0
  return Math.round(total / count)
}

/**
 * "Ending soon" = any of the worker's current engagements drops out within the next
 * `lookaheadMonths` months. New engagements appearing in future months don't flag the
 * worker — only departures.
 */
export function isEndingSoon(cells: WorkerMonthCell[], lookaheadMonths: number): boolean {
  const currentIdx = cells.findIndex(c => c.isCurrentMonth)
  if (currentIdx === -1) return false
  const currentCell = cells[currentIdx]
  const currentIds = new Set(currentCell.activeEngagementIds)
  if (currentIds.size === 0) return false
  const lookahead = cells.slice(currentIdx + 1, currentIdx + 1 + lookaheadMonths)
  return lookahead.some(cell => {
    if (cell.activeEngagementIds.length === 0) return true
    const futureIds = new Set(cell.activeEngagementIds)
    for (const id of currentIds) {
      if (!futureIds.has(id)) return true
    }
    return false
  })
}

export function endingSoonCount(workers: WorkerView[], lookaheadMonths: number): number {
  return workers.filter(w => isEndingSoon(w.months, lookaheadMonths)).length
}

/**
 * Count of workers with at least one engagement that's active at the start of the window
 * but missing by some month before / at the window's end. Anchored on the first in-range
 * cell rather than "current month" so the metric tracks whichever heatmap window the user
 * is viewing — including ranges that are fully past or fully future.
 */
export function endingInRangeCount(
  workers: WorkerView[],
  startIso: string,
  endIso: string
): number {
  return workers.filter(w => {
    const inRange = w.months.filter(c => c.monthStart >= startIso && c.monthStart <= endIso)
    if (inRange.length < 2) return false
    const startIds = new Set(inRange[0].activeEngagementIds)
    if (startIds.size === 0) return false
    for (let i = 1; i < inRange.length; i++) {
      const cellIds = new Set(inRange[i].activeEngagementIds)
      for (const id of startIds) {
        if (!cellIds.has(id)) return true
      }
    }
    return false
  }).length
}

/**
 * Count of workers whose availability in the given month meets or exceeds the threshold %.
 * Threshold is inclusive (>=) so 100 cleanly covers the "fully unallocated" case while
 * lower values ("at least 50% free") capture workers who are only partially booked.
 */
export function availableHeadcountAt(
  workers: WorkerView[],
  monthIndex: number,
  thresholdPct: number
): number {
  return workers.filter(w => {
    const cell = w.months[monthIndex]
    if (!cell) return false
    return cell.availablePercent >= thresholdPct
  }).length
}

/**
 * Count of workers who hit the threshold in *any* cell whose `monthStart` falls in
 * `[startIso, endIso]`. Surfaces "how many workers will be at least N% free at some point
 * in the window" — a planning lens for staffing windows that opens up at irregular times,
 * not just at end-of-range.
 */
export function availableHeadcountInRange(
  workers: WorkerView[],
  startIso: string,
  endIso: string,
  thresholdPct: number
): number {
  return workers.filter(w =>
    w.months.some(
      cell =>
        cell.monthStart >= startIso &&
        cell.monthStart <= endIso &&
        cell.availablePercent >= thresholdPct
    )
  ).length
}

// Skill and proficiency-level selections pair on the same skill entry: React + Expert means one
// entry that is both, not React on one entry and Expert on another. Either selection on its own
// still matches any entry carrying it.
export function matchesSkillAndProficiency(
  skills: WorkerSkillEntry[] | undefined,
  selectedSkillIds: Set<string>,
  selectedProficiencyLevelIds: Set<string>
): boolean {
  if (selectedSkillIds.size === 0 && selectedProficiencyLevelIds.size === 0) {
    return true
  }

  return (skills ?? []).some(
    skill =>
      (selectedSkillIds.size === 0 || selectedSkillIds.has(String(skill.skillId))) &&
      (selectedProficiencyLevelIds.size === 0 ||
        (skill.proficiencyLevelId !== undefined &&
          selectedProficiencyLevelIds.has(String(skill.proficiencyLevelId))))
  )
}
